import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideStep,
  GuideSteps,
  GuideTip,
} from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function RequestRoadInspectionRecordsGuide() {
  return (
    <GuideLayout
      slug="request-road-inspection-records"
      category="Evidence"
      categoryColor="accent"
      title="Request Council Road Inspection Records"
      seoTitle="Request Council Road Inspection Records | Fixtyer"
      description="A practical EIR and FOI guide with focused templates for highway inspection, defect-report and repair records."
      readTime="8 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'Environmental Information Regulations 2004',
          url: 'https://www.legislation.gov.uk/uksi/2004/3391/part/2',
        },
        {
          name: 'DfT access to information guidance',
          url: 'https://www.gov.uk/government/publications/accessing-information-from-any-part-of-dft/accessing-information-from-any-part-of-dft',
        },
        {
          name: 'Devon published road-record response',
          url: 'https://www.devon.gov.uk/accesstoinformation/information_request/plymouth-road-between-totnes-and-the-a38-via-the-blue-post-garage-and-avonwick/',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'Ask the responsible highway authority for recorded inspection, defect and repair information for a tightly defined location and date range.',
        },
        {
          label: 'Likely regime',
          value:
            'Road-condition and maintenance records are commonly handled under the Environmental Information Regulations 2004 (EIR), even if a request mentions FOI.',
        },
        {
          label: 'Normal deadline',
          value:
            'EIR regulation 5 ordinarily requires information as soon as possible and no later than 20 working days, subject to the Regulations.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'section-58-defence',
          title: 'Section 58 Defence',
          readTime: '7 min',
        },
        {
          slug: 'prior-reports-explained',
          title: 'Prior Reports Explained',
          readTime: '5 min',
        },
        {
          slug: 'pothole-claim-rejected-next-steps',
          title: 'Rejection Next Steps',
          readTime: '8 min',
        },
      ]}
    >
      <GuideSteps>
        <GuideStep number={1} title="Define one location and useful time window">
          Give the road name, nearest property or junction, direction of travel and coordinates or
          what3words if available. Ask for a proportionate period around the incident rather than
          every record for a whole network.
        </GuideStep>
        <GuideStep number={2} title="Ask for recorded information, not an explanation">
          Request existing policies, completed records, logs and correspondence. EIR and the
          Freedom of Information Act 2000 provide access to recorded information; they do not
          require an authority to create an opinion or answer a hypothetical question.
        </GuideStep>
        <GuideStep number={3} title="Keep the request and response together">
          Save the sent message, acknowledgement, clarification requests, disclosed files and any
          refusal notice. Compare the completed records with the relevant policy without assuming a
          missing entry alone decides liability.
        </GuideStep>
      </GuideSteps>

      <GuideSection title="Focused EIR request template" icon={<span aria-hidden="true">EIR</span>}>
        <div className="rounded-xl bg-surface-50 p-5 font-mono text-sm leading-relaxed">
          <p>Subject: Environmental information request — [road and location]</p>
          <p className="mt-4">Please provide the following recorded information for [precise location] covering [start date] to [end date]:</p>
          <ol className="mt-3 list-decimal space-y-2 pl-5">
            <li>the highway safety-inspection policy and road hierarchy applicable to this location;</li>
            <li>completed safety-inspection records, including dates, method and recorded findings;</li>
            <li>defect reports and repair records, including receipt, assessment, priority and completion dates;</li>
            <li>records of complaints or reports matched to the specified location; and</li>
            <li>any location-specific risk assessment or temporary warning record.</li>
          </ol>
          <p className="mt-4">
            Please treat this as a request under the Environmental Information Regulations 2004,
            or under the Freedom of Information Act 2000 to the extent you consider any part is not
            environmental information. Electronic copies are preferred.
          </p>
        </div>
      </GuideSection>

      <GuideSection title="If the response is late, broad or refused" icon={<span aria-hidden="true">?</span>}>
        <p className="mb-4">
          Check whether the authority asked for clarification or relied on a specific exception.
          Narrow an overly broad request if that will produce the location-specific records you
          need. If dissatisfied, follow the internal-review instructions in the response and then
          the Information Commissioner&apos;s published route where appropriate.
        </p>
        <GuideTip variant="warning" title="Do not overstate third-party reports">
          A FixMyStreet entry is a research lead. Verify the same defect, location and date,
          responsible authority, status and receipt against the authority’s own records.
        </GuideTip>
        <p>
          Then use the{' '}
          <Link to="/guides/section-58-defence" className={inlineLink}>
            Section 58 guide
          </Link>{' '}
          to understand how the disclosed records may fit into the authority&apos;s response.
        </p>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
